/**
 * useWorkflowConfig — per-org, per-relationship, per-request-type settings.
 *
 * Authority roles (PM, strata_admin) can toggle which request types their
 * subordinates can raise, set SLA expectations, and control auto-approval.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { RequestType, RequestRelationship } from "@/hooks/useRequests";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorkflowConfig {
  id: string;
  org_id: string;
  relationship: RequestRelationship;
  request_type: RequestType;
  enabled: boolean;
  auto_approve: boolean;
  requires_payment: boolean;
  notify_authority_on_submit: boolean;
  notify_requester_on_update: boolean;
  response_sla_hours: number | null;
  custom_label: string | null;
  custom_description: string | null;
  updated_at: string;
}

// Default fallback for when migration hasn't been applied yet
const DEFAULT_CONFIG: Omit<WorkflowConfig, "id" | "org_id" | "updated_at"> = {
  relationship: "guest_to_host",
  request_type: "inquiry",
  enabled: true,
  auto_approve: false,
  requires_payment: false,
  notify_authority_on_submit: true,
  notify_requester_on_update: true,
  response_sla_hours: 48,
  custom_label: null,
  custom_description: null,
};

// ─── Relationship display labels ──────────────────────────────────────────────

export const RELATIONSHIP_LABELS: Record<string, string> = {
  guest_to_host:    "Guest → Host",
  host_to_pm:       "Host → Property Manager",
  host_to_strata:   "Host → Strata Council",
  pm_to_strata:     "Property Manager → Strata",
  strata_to_host:   "Strata → Host",
  pm_to_host:       "PM → Host",
};

// ─── Fetch all configs for an org ─────────────────────────────────────────────

export function useWorkflowConfigs(relationship?: RequestRelationship) {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["workflow_configs", activeOrgId, relationship],
    enabled: !!activeOrgId,
    retry: (failureCount, err: any) => {
      // Don't retry if table doesn't exist yet (migration pending)
      const msg = String(err?.message ?? "");
      if (msg.includes("does not exist") || msg.includes("42P01") || msg.includes("404")) return false;
      return failureCount < 2;
    },
    queryFn: async () => {
      let q = supabase
        .from("workflow_configs")
        .select("*")
        .eq("org_id", activeOrgId!);
      if (relationship) q = q.eq("relationship", relationship);
      const { data, error } = await q;
      if (error) {
        console.warn("[useWorkflowConfigs] table not available yet:", error.message);
        return [] as WorkflowConfig[];
      }
      return (data ?? []) as WorkflowConfig[];
    },
  });
}

/** Returns a single config entry or a safe default if not found */
export function useWorkflowConfig(relationship: RequestRelationship, requestType: RequestType) {
  const { data: all = [], ...rest } = useWorkflowConfigs(relationship);
  const config = all.find(
    (c) => c.relationship === relationship && c.request_type === requestType
  ) ?? null;
  return { config, ...rest };
}

// ─── Upsert a config ──────────────────────────────────────────────────────────

export function useUpsertWorkflowConfig() {
  const queryClient = useQueryClient();
  const { activeOrgId, user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (
      updates: Partial<WorkflowConfig> & {
        relationship: RequestRelationship;
        request_type: RequestType;
      }
    ) => {
      if (!activeOrgId) throw new Error("No active organization");

      const { data, error } = await supabase
        .from("workflow_configs")
        .upsert(
          {
            ...updates,
            org_id: activeOrgId,
            created_by: user?.id,
          },
          { onConflict: "org_id,relationship,request_type" }
        )
        .select()
        .single();

      if (error) throw error;
      return data as WorkflowConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow_configs", activeOrgId] });
      toast({ title: "Workflow setting saved" });
    },
    onError: (e: Error) =>
      toast({ title: "Error saving workflow config", description: e.message, variant: "destructive" }),
  });
}

// ─── Helper: check if a request type is enabled for a relationship ────────────

/**
 * Returns whether a given request type is enabled for a relationship context.
 * Falls back to TRUE (permissive default) if configs haven't been loaded yet.
 */
export function isRequestTypeEnabled(
  configs: WorkflowConfig[],
  relationship: RequestRelationship,
  requestType: RequestType
): boolean {
  const config = configs.find(
    (c) => c.relationship === relationship && c.request_type === requestType
  );
  return config ? config.enabled : true; // default open
}

/**
 * Returns all enabled request types for a given relationship.
 */
export function getEnabledRequestTypes(
  configs: WorkflowConfig[],
  relationship: RequestRelationship
): RequestType[] {
  const ALL_TYPES: RequestType[] = [
    "maintenance", "dispute", "amenity", "document", "inquiry", "compliance",
  ];
  return ALL_TYPES.filter((t) => isRequestTypeEnabled(configs, relationship, t));
}
